import Vue from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import store from './store'
import VModal from 'vue-js-modal'
import axios from 'axios'
import '@/assets/scss/app.scss'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import b2cAuthService from '@/auth/b2cAuthService'

Vue.config.productionTip = false

require('@/store/subscriber')

// Install VModal
Vue.use(VModal, {
  dynamic: true,
  dynamicDefaults: {
    height: 'auto',
    scrollable: true,
    classes: 'outer-modal'
  }
})

Vue.use(Toast, {
  transition: 'Vue-Toastification__bounce',
  maxToasts: 20,
  newestOnTop: true,
  position: 'bottom-right',
  timeout: 4000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: 'button',
  icon: true,
  rtl: false
})

Vue.use(require('vue-moment'))

axios.defaults.baseURL = process.env.VUE_APP_AXIOS_BASE_URL
axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*'

axios.interceptors.request.use(
  async config => {
    try {
      const token = await b2cAuthService.acquireTokenSilent()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.warn('Could not acquire token for request:', error)
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor for handling auth errors
axios.interceptors.response.use(
  response => {
    if (response.status === 403 || response.status === 401) {
      return Promise.reject(response)
    }
    return Promise.resolve(response)
  },

  async error => {
    if (error.response && error.response.status) {
      switch (error.response.status) {
        case 401:
          console.log('The token is invalid')
          store.dispatch('UserStore/logout')
        break

        case 403:
          console.log('This content is forbidden. Logging out..')
          store.dispatch('UserStore/logout')
        break
      }
      return Promise.reject(error.response)
    } else {
      // network error
      console.log('A network error occured')
    }
})

// check if endpoint has been overridden
const endpointOverride = localStorage.getItem('override_endpoint')
if(endpointOverride) {
  axios.defaults.baseURL = endpointOverride
  store.commit('AppStore/SET_ENDPOINT_IS_OVERRIDDEN', true)
}

// Initialize the application with B2C
async function initializeApp() {
  try {
    const response = await b2cAuthService.initialize()
    console.log('B2C authentication service initialized')

    // If we have a redirect response (user just logged in), handle it
    if (response) {
      const accessToken = response.accessToken
      const idToken = response.idToken
      const userInfo = b2cAuthService.getUserInfo()
      store.commit('UserStore/SET_B2C_AUTH_DATA', {
        username: userInfo.username,
        accessToken: accessToken,
        userInfo: userInfo,
        idToken: idToken,
        account: response.account
      })
    } else {
      // Check if user is already authenticated
      const account = b2cAuthService.getAccount()
      if (account) {
        // User has existing session, get token silently
        try {
          const accessToken = await b2cAuthService.acquireTokenSilent()
          const userInfo = b2cAuthService.getUserInfo()

          store.commit('UserStore/SET_B2C_AUTH_DATA', {
            username: userInfo.username,
            accessToken: accessToken,
            userInfo: userInfo,
            account: account
          })
        } catch (error) {
          console.error('Failed to acquire token silently:', error)
          // Token acquisition failed, will redirect to B2C login
          await b2cAuthService.signInRedirect()
          return // Don't mount app yet, will redirect
        }
      } else {
        // No existing session, redirect to B2C login
        console.log('No authenticated user found, redirecting to B2C login...')
        await b2cAuthService.signInRedirect()
        return // Don't mount app yet, will redirect
      }
    }
  } catch (error) {
    console.error('Failed to initialize B2C:', error)
    // If initialization fails, try to redirect to B2C login anyway
    try {
      return await b2cAuthService.signInRedirect()
    } catch (redirectError) {
      console.error('Failed to redirect to B2C login:', redirectError)
    }
  }

  // Mount Vue application
  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount('#app')
}

// Start the application
initializeApp()